import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import React from "react";
import { Link } from "react-router-dom";
import DateTimeUtils from "src/Shared/Tools/DateTimeUtils";
import { IOnboardingWizardState } from "../../Interfaces/IOnboardingWizardState";
import {
  CAPTURE_ON_RECORD,
  emDashWhenBlank,
  ONBOARDING_STEP_LABELS,
  STEP_REQUIREMENTS,
} from "./OnboardingWizard.utils";
import CompanyOfficeGrid from "../CompanyOfficeGrid/CompanyOfficeGrid";
import CompanyContactGrid from "../CompanyContactGrid/CompanyContactGrid";
import ContactDistributionSettings from "./ContactDistributionSettings";
import { WriteThroughResult } from "./useWriteThrough";
import Step1ClientInformation from "./Step1ClientInformation";
import Step2Services from "./Step2/Step2Services";
import Step5Invoicing from "./Step5Invoicing";
import Step6SiteRequirements from "./Step6SiteRequirements";

type Props = {
  state: IOnboardingWizardState;
  step: number;
  companyRowID: string;
  onChanged: () => void;
  /** Lets a capture form hand the shell a way to flush pending write-throughs before validating. */
  registerFlush: (flush: () => Promise<WriteThroughResult>) => void;
  /**
   * Lets a capture form answer for its own validity from its live fields, before anything is
   * written. A step that leaves this unused is judged on the server's reply instead, as before.
   */
  registerValidate?: (validate: (() => string | null) | null) => void;
  /** Marks the fields blocking an advance, once one has been refused (bug 5668). */
  showFieldErrors?: boolean;
};

const Field: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <Grid item xs={12} md={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{emDashWhenBlank(value)}</Typography>
  </Grid>
);

/**
 * One wizard step's frame (Feature #5586), holding that step's capture surface.
 *
 * Steps 1, 3 and 4 write to the client record — the record and the wizard share one stored value per
 * field, so nothing is asked twice (P.6). Steps 2, 5 and 6 write to the onboarding's own payload.
 *
 * Every control is disabled once the onboarding is submitted (story 5592).
 */
const OnboardingStepPanel: React.FC<Props> = ({
  state,
  step,
  companyRowID,
  onChanged,
  registerFlush,
  registerValidate,
  showFieldErrors,
}) => {
  const complete = Boolean(state.stepsComplete?.[step - 1]);
  const frozen = state.isSubmitted;
  const captureOnRecord = CAPTURE_ON_RECORD[step];

  return (
    <Paper elevation={0} sx={{ p: 2, border: "1px solid #e0e0e0" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {complete ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <RadioButtonUncheckedIcon fontSize="small" />
          )}
          <Typography variant="subtitle1" fontWeight={600}>
            Step {step} — {ONBOARDING_STEP_LABELS[step - 1]}
          </Typography>
          <Chip
            size="small"
            label={complete ? "Complete" : "Incomplete"}
            color={complete ? "success" : "default"}
          />
        </Box>
        {captureOnRecord && (
          <Button
            size="small"
            variant="outlined"
            component={Link}
            replace
            to={`/company/${companyRowID}/${captureOnRecord.path}`}
          >
            {captureOnRecord.label}
          </Button>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {STEP_REQUIREMENTS[step - 1]}
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Steps 1, 5 and 6 capture into the shared record and the onboarding payload; steps 3 and 4
          are captured on the client record's own Offices and Contacts sections until their wizard
          screens land with the rest of this feature. */}
      {step === 1 && (
        <Step1ClientInformation
          showFieldErrors={showFieldErrors}
          state={state}
          companyRowID={companyRowID}
          frozen={frozen}
          registerFlush={registerFlush}
          registerValidate={registerValidate}
        />
      )}

      {step === 2 && (
        <>
          {!state.isHealthTickEligible && (
            <Alert severity="info" sx={{ mb: 2 }}>
              HealthTick is offered to New Zealand clients only, so the HealthTick half
              of this step does not apply to this client.
            </Alert>
          )}

          {/* The HealthTick review card is rendered inside Step2Services itself, against that
              component's own live service-line state rather than this shell's snapshot of it — see
              the comment there (bug 5833). */}
          <Step2Services
            state={state}
            companyRowID={companyRowID}
            frozen={frozen}
            registerFlush={registerFlush}
            onChanged={onChanged}
          />
        </>
      )}

      {/* The same site editor the client record uses, over the same stored list (S3.10): Add,
          Import, Edit and Remove are disabled once submitted, and the template download stays. */}
      {/* In the wizard every site also needs the Imperans clinic that will service it, picked from
          the paired Imperans's list for the client's country (Epic 6303, story 6373). */}
      {step === 3 && (
        <CompanyOfficeGrid
          subMenuRender
          readOnly={frozen}
          countryCode={state.countryCode}
          requireServicingClinic
        />
      )}

      {/* The same contact editor, with the wizard's stricter rule that an Imperans role must be
          chosen before a contact saves here (S4.1). */}
      {step === 4 && (
        <>
          <CompanyContactGrid subMenuRender readOnly={frozen} requireImperansRole />
          <ContactDistributionSettings
            showFieldErrors={showFieldErrors}
            state={state}
            companyRowID={companyRowID}
            frozen={frozen}
            registerFlush={registerFlush}
          />
        </>
      )}

      {step === 5 && (
        <Step5Invoicing
          showFieldErrors={showFieldErrors}
          state={state}
          companyRowID={companyRowID}
          frozen={frozen}
          registerFlush={registerFlush}
        />
      )}

      {step === 6 && (
        <Step6SiteRequirements
          state={state}
          companyRowID={companyRowID}
          frozen={frozen}
          registerFlush={registerFlush}
        />
      )}
    </Paper>
  );
};

export default OnboardingStepPanel;
